import { useState, useEffect } from "react";
import { Apis } from "../../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.commercialArchitectureDesigns)
      ? payload.commercialArchitectureDesigns
      : Array.isArray(payload)
        ? payload
        : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return { items, pagination, totalCount, payload };
};

// --- Commercial Architecture Design ---
export const useCommercialArchitectureDesign = () => {
  const getCommercialArchitectureDesign = async (
    setCommercialArchitectureDesignData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
  ) => {
    try {
      const params = {};

      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }

      const response = await Apis.getCommercialArchitectureDesign(offset, limit, params);
      if (response?.status === 200) {
        const { items, pagination, totalCount, payload } = unwrapListPayload(
          response?.data,
        );
        setCommercialArchitectureDesignData(items);
        setTotal(totalCount);
        return { data: items, pagination, totalCount, ...payload };
      }
      throw new Error(
        response?.data?.message || "Failed to fetch commercial architecture designs.",
      );
    } catch (error) {
      console.error("Error fetching commercial architecture design:", error);
      throw error;
    }
  };

  const searchCommercialArchitectureDesign = async (searchFilter, offset = 0, limit = 10) => {
    try {
      const params = {};

      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }

      const response = await Apis.getCommercialArchitectureDesign(offset, limit, params);
      const { items, pagination, totalCount } = unwrapListPayload(
        response?.data,
      );
      return { data: items, pagination, totalCount };
    } catch (error) {
      console.error(
        "🚀 ~ file: useHook.js ~ line 64 ~ searchCommercialArchitectureDesign ~ error",
        error,
      );
      throw error;
    }
  };

  const addCommercialArchitectureDesign = async (data) => {
    try {
      const response = await Apis.addCommercialArchitectureDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error adding commercial architecture design:", error);
      throw new Error(getApiError(error, "Failed to add commercial architecture design."));
    }
  };

  const updateCommercialArchitectureDesign = async (id, data) => {
    try {
      const response = await Apis.updateCommercialArchitectureDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating commercial architecture design:", error);
      throw new Error(getApiError(error, "Failed to update commercial architecture design."));
    }
  };

  const deleteCommercialArchitectureDesign = async (id) => {
    try {
      const response = await Apis.deleteCommercialArchitectureDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting commercial architecture design:", error);
      throw new Error(getApiError(error, "Failed to delete commercial architecture design."));
    }
  };

  const getCommercialArchitectureDesignById = async (id, setHouseData) => {
    try {
      const response = await Apis.getCommercialArchitectureDesignById(id);
      const payload = unwrapResponseBody(response?.data);
      if (typeof setHouseData === "function") setHouseData(payload);
      return payload;
    } catch (error) {
      console.error("Error fetching commercial architecture design by id:", error);
      throw error;
    }
  };

  const showCommercialArchitectureDesignOnHomeScreen = async (commercialArchitectureDesignId, homeScreen) => {
    try {
      const data = {
        commercialArchitectureDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addCommercialArchitectureDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateCommercialArchitectureDesignPosition = async (commercialArchitectureDesignId, newPosition) => {
    try {
      const response = await Apis.updateCommercialArchitectureDesignPosition({
        commercialArchitectureDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating commercial architecture design position:", error);
      throw error;
    }
  };

  const updateCommercialArchitectureDesignPrimaryImage = async (id) => {
    try {
      const response = await Apis.updateCommercialArchitectureDesignPrimaryImage(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating commercial architecture design primary image:", error);
      throw error;
    }
  };

  return {
    getCommercialArchitectureDesign,
    addCommercialArchitectureDesign,
    updateCommercialArchitectureDesign,
    deleteCommercialArchitectureDesign,
    getCommercialArchitectureDesignById,
    showCommercialArchitectureDesignOnHomeScreen,
    searchCommercialArchitectureDesign,
    updateCommercialArchitectureDesignPosition,
    updateCommercialArchitectureDesignPrimaryImage,
  };
};
